import {
  MULTIPLE_CHARTS_OUTPUT_STEP_DATA_NAME,
  SaveToResultsStepDataName,
  SendToDocumentStepDataName,
  SINGLE_CHART_OUTPUT_STEP_DATA_NAME,
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {
  getIconAndTextForChartViewStep,
  getIconAndTextForRegressionStep,
} from 'analysis-tool/charting/AnalysisChartingUtils';
import {findAndAssertSingleStepChart} from 'analysis-tool/data/findAndAssertStepDataCharts';
import {
  findAndAssertSingleStepDataFrame,
  findAndAssertStepData,
} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import {assert} from 'Asserts';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineGraphQL, PipelineStepConfig} from 'pipelines/PipelineTypes';
import {getParentSteps} from 'pipelines/StepLineageUtils';
import {BenchlingDuotoneIconName} from 'ui-library/icons/BenchlingDuotoneIcon';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {
  DEFAULT_NEW_SECTION_TITLE,
  PresentationalOutputDocumentLayoutConfiguration,
} from './OutputDocumentLayout';
import {getSendToDocumentStepConfig} from './OutputsUtil';
import {AnalysisOutput, OutputDocumentLayoutItemType, OutputDocumentLayoutConfiguration} from './Types';

export const INITIAL_DOCUMENT_LAYOUT_CONFIGURATION: OutputDocumentLayoutConfiguration = [
  {
    type: OutputDocumentLayoutItemType.DOCUMENT_SECTION_TITLE,
    title: DEFAULT_NEW_SECTION_TITLE,
  },
];

export function getUpdatedDocumentLayoutConfigurationFromNewSelectedOutputs(
  originalDocumentLayoutConfiguration: OutputDocumentLayoutConfiguration,
  previousSelectedOutputs: Array<AnalysisOutput>,
  newSelectedOutputs: Array<AnalysisOutput>
): OutputDocumentLayoutConfiguration {
  const newDataFrameIds = new Set(newSelectedOutputs.map((output) => output.selectorIndex));
  const previousSelectorIndices = new Set(previousSelectedOutputs.map((output) => output.selectorIndex));

  return originalDocumentLayoutConfiguration
    .filter(
      (item) =>
        item.type === OutputDocumentLayoutItemType.DOCUMENT_SECTION_TITLE ||
        newDataFrameIds.has(item.selectorIndex)
    )
    .map((item) => {
      if (item.type === OutputDocumentLayoutItemType.DOCUMENT_SECTION_TITLE) {
        return item;
      }
      const newSelectedOutput = newSelectedOutputs.find(
        (output) => output.selectorIndex === item.selectorIndex
      )!;
      return {
        ...item,
        icon: newSelectedOutput.outputConfig.icon,
      };
    })
    .concat(
      newSelectedOutputs
        .filter((output) => !previousSelectorIndices.has(output.selectorIndex))
        .map((output) => ({
          type: OutputDocumentLayoutItemType.DOCUMENT_CONTENT,
          icon: output.outputConfig.icon,
          outputConfig: output.outputConfig,
          selectorIndex: output.selectorIndex,
        }))
    );
}
type SectionLayoutConfig = PipelineStepConfig<PipelineStepType.SEND_TO_DOCUMENT, 0>['sectionLayout'];

export function getDocumentSectionLayoutConfig(
  chartOutputs: Array<AnalysisOutput>,
  datasetOutputs: Array<AnalysisOutput>,
  resultOutputs: Array<AnalysisOutput>,
  documentLayoutConfiguration: OutputDocumentLayoutConfiguration
): SectionLayoutConfig {
  const documentSectionLayout: SectionLayoutConfig = [];

  documentLayoutConfiguration.forEach((layoutItem) => {
    if (layoutItem.type === OutputDocumentLayoutItemType.DOCUMENT_SECTION_TITLE) {
      documentSectionLayout.push({sectionName: layoutItem.title, inputReferences: []});
    } else if (datasetOutputs.some((output) => output.selectorIndex === layoutItem.selectorIndex)) {
      documentSectionLayout[documentSectionLayout.length - 1].inputReferences.push({
        inputDataName: SendToDocumentStepDataName.INPUT_DATASETS,
        inputDataIndex: datasetOutputs.findIndex(
          (output) => output.selectorIndex === layoutItem.selectorIndex
        ),
        groupKey: null,
      });
    } else if (chartOutputs.some((output) => output.selectorIndex === layoutItem.selectorIndex)) {
      documentSectionLayout[documentSectionLayout.length - 1].inputReferences.push({
        inputDataName: SendToDocumentStepDataName.INPUT_CHARTS,
        inputDataIndex: chartOutputs.findIndex((output) => output.selectorIndex === layoutItem.selectorIndex),
        groupKey: null,
      });
    } else {
      documentSectionLayout[documentSectionLayout.length - 1].inputReferences.push({
        inputDataName: SendToDocumentStepDataName.INPUT_SAVED_RESULTS,
        inputDataIndex: resultOutputs.findIndex(
          (output) => output.selectorIndex === layoutItem.selectorIndex
        ),
        groupKey: null,
      });
    }
  });

  return documentSectionLayout;
}

const STEP_TYPE_TO_SEND_TO_DOCUMENT_STEP_DATA_NAME = {
  [PipelineStepType.CHART_VIEW]: SendToDocumentStepDataName.INPUT_CHARTS,
  [PipelineStepType.USER_DEFINED_FUNCTION]: SendToDocumentStepDataName.INPUT_CHARTS,
  [PipelineStepType.EXPORT_DATA_CATALOG_DATASET]: SendToDocumentStepDataName.INPUT_DATASETS,
  [PipelineStepType.IMPORT_PLATES]: SendToDocumentStepDataName.INPUT_PLATES,
  [PipelineStepType.SAVE_TO_RESULTS]: SendToDocumentStepDataName.INPUT_SAVED_RESULTS,
};

function getIconFromParentStep(step: GetAnalysisPipelineStep): BenchlingDuotoneIconName | BenchlingIconName {
  if (step.definition.stepType === PipelineStepType.SAVE_TO_RESULTS) {
    return 'table' as const;
  } else if (step.definition.stepType === PipelineStepType.EXPORT_DATA_CATALOG_DATASET) {
    return 'dataset' as const;
  } else if (step.definition.stepType === PipelineStepType.CHART_VIEW) {
    return getIconAndTextForChartViewStep(step).icon;
  } else if (step.definition.stepType === PipelineStepType.USER_DEFINED_FUNCTION) {
    return 'chart' as const;
  } else if (step.definition.stepType === PipelineStepType.IMPORT_PLATES) {
    return 'plate' as const;
  } else if (step.definition.stepType === PipelineStepType.REGRESSION) {
    return getIconAndTextForRegressionStep(step).icon;
  }
  assert(false, `Unexpected step type: ${step.definition.stepType}`);
}

/**
 * Return the layout items to be used in PresentationalOutputDocumentLayout for a given pipeline.
 *
 * The names of the items are generated as follows:
 *   - Chart outputs use the chart step's output chart(s) name(s)
 *   - Dataset outputs use the export dataset step's input dataset name
 *   - Result outputs use the save to result step's input dataset name
 */
export function getPresentationalDocumentLayoutConfiguration(
  pipeline: PipelineGraphQL
): PresentationalOutputDocumentLayoutConfiguration | null {
  const sendToDocumentStep = pipeline.execution.steps.find(
    (step) => step.definition.stepType === PipelineStepType.SEND_TO_DOCUMENT
  );

  if (sendToDocumentStep == null) {
    return null;
  }

  const parentSteps = getParentSteps(pipeline, sendToDocumentStep);
  const orderedParentStepsByStepDataName: {
    [key in SendToDocumentStepDataName]: Array<GetAnalysisPipelineStep>;
  } = {
    [SendToDocumentStepDataName.INPUT_CHARTS]: [],
    [SendToDocumentStepDataName.INPUT_DATASETS]: [],
    [SendToDocumentStepDataName.INPUT_SAVED_RESULTS]: [],
    [SendToDocumentStepDataName.INPUT_PLATES]: [],
  };
  sendToDocumentStep.definition.incomingEdges.forEach((edge) => {
    const parentStep = parentSteps.find((parentStep) => parentStep.id === edge.parent.step.id)!;
    if (parentStep.definition.stepType === PipelineStepType.REGRESSION) {
      if (edge.parentStepData.name === 'Output chart') {
        orderedParentStepsByStepDataName[SendToDocumentStepDataName.INPUT_CHARTS].push(parentStep);
      } else {
        orderedParentStepsByStepDataName[SendToDocumentStepDataName.INPUT_DATASETS].push(parentStep);
      }
    } else {
      orderedParentStepsByStepDataName[
        STEP_TYPE_TO_SEND_TO_DOCUMENT_STEP_DATA_NAME[
          parentStep.definition.stepType as keyof typeof STEP_TYPE_TO_SEND_TO_DOCUMENT_STEP_DATA_NAME
        ]
      ].push(parentStep);
    }
  });

  const sectionLayout = getSendToDocumentStepConfig(sendToDocumentStep).sectionLayout;
  return sectionLayout.flatMap((section) => [
    {type: OutputDocumentLayoutItemType.DOCUMENT_SECTION_TITLE, title: section.sectionName},
    ...section.inputReferences.map((inputReference) => {
      const parentSteps = orderedParentStepsByStepDataName[inputReference.inputDataName];
      const parentStep = parentSteps[inputReference.inputDataIndex];
      assert(
        parentStep != null,
        `Missing parent step for ${inputReference.inputDataName} at index ${inputReference.inputDataIndex}`
      );
      let label: string;

      if (
        inputReference.inputDataName === SendToDocumentStepDataName.INPUT_CHARTS ||
        inputReference.inputDataName === SendToDocumentStepDataName.INPUT_DATASETS
      ) {
        if (inputReference.inputDataName === SendToDocumentStepDataName.INPUT_CHARTS) {
          const stepData =
            parentStep.definition.stepType === PipelineStepType.USER_DEFINED_FUNCTION
              ? findAndAssertStepData(parentStep, MULTIPLE_CHARTS_OUTPUT_STEP_DATA_NAME, false)
              : findAndAssertStepData(parentStep, SINGLE_CHART_OUTPUT_STEP_DATA_NAME, false);
          if (stepData.charts.length === 0) {
            label = 'Input chart';
          } else if (parentStep.definition.stepType === PipelineStepType.USER_DEFINED_FUNCTION) {
            label = findAndAssertSingleStepChart(parentStep, MULTIPLE_CHARTS_OUTPUT_STEP_DATA_NAME).name;
          } else {
            label = findAndAssertSingleStepChart(parentStep, SINGLE_CHART_OUTPUT_STEP_DATA_NAME).name;
          }
        } else {
          const stepData = findAndAssertStepData(parentStep, SINGLE_DATASET_INPUT_STEP_DATA_NAME, true);
          if (stepData.dataFrames.length === 0) {
            label = 'Input table';
          } else {
            label = findAndAssertSingleStepDataFrame(
              parentStep,
              SINGLE_DATASET_INPUT_STEP_DATA_NAME,
              true
            ).name;
          }
        }
      } else if (inputReference.inputDataName === SendToDocumentStepDataName.INPUT_SAVED_RESULTS) {
        const stepData = findAndAssertStepData(parentStep, SaveToResultsStepDataName.INPUT_DATASET, true);
        if (stepData.dataFrames.length === 0) {
          label = 'Input table';
        } else {
          label = findAndAssertSingleStepDataFrame(
            parentStep,
            SaveToResultsStepDataName.INPUT_DATASET,
            true
          ).name;
        }
      } else {
        label = 'Input plate';
      }
      return {
        type: OutputDocumentLayoutItemType.DOCUMENT_CONTENT as const,
        icon: getIconFromParentStep(parentStep),
        label,
      };
    }),
  ]);
}
